import {
  Check,
  ChevronRight,
  FileCheck2,
  FileUp,
  History,
  ListChecks,
  RefreshCw,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import LeaveDocumentAnalysisSummary from "../components/LeaveDocumentAnalysisSummary";
import PageHeader from "../components/PageHeader";
import { useWorkspace } from "../context/WorkspaceContext";
import type { LeaveType, Paginated } from "../types/api";
import { unwrapResults } from "../types/api";
import type { LeaveDocument, LeaveDocumentListRow } from "../types/leaveDocument";
import { periodLabel } from "../types/leaveDocument";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate, organizationQuery } from "../utils/format";

type Document = LeaveDocument;

const hours = (minutes?: number) => `${((minutes ?? 0) / 60).toFixed(2)} uur`;

// Geen verloftype-id, maar een expliciete keuze om deze documentregel over te
// slaan. Alleen hier bruikbaar; de verlofplanner kent deze keuze niet.
const IGNORE_OPTION = "ignore";

export default function LeaveDocumentPage() {
  const { organizationId } = useWorkspace();
  const [file, setFile] = useState<File | null>(null);
  const [document, setDocument] = useState<Document | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mappingSelections, setMappingSelections] = useState<Record<number, string>>(
    {},
  );
  const [history, setHistory] = useState<LeaveDocumentListRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const analysis = useMemo(() => document?.analyses?.[0] ?? null, [document]);

  const loadHistory = useCallback(async () => {
    const organizationPart = organizationQuery(organizationId);
    setHistoryLoading(true);
    try {
      const response = await api.get<
        Paginated<LeaveDocumentListRow> | LeaveDocumentListRow[]
      >(`/leave/documents/${organizationPart ? `?${organizationPart}` : ""}`);
      setHistory(unwrapResults(response.data));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    setMappingSelections({});
  }, [document?.id, analysis]);

  useEffect(() => {
    void api
      .get("/leave/types/?scope=all")
      .then((response) => {
        setLeaveTypes(
          Array.isArray(response.data) ? response.data : response.data.results,
        );
      })
      .catch(() => setLeaveTypes([]));
  }, [organizationId]);

  const refresh = async (id: number) => {
    setDocument((await api.get<Document>(`/leave/documents/${id}/`)).data);
    await loadHistory();
  };
  const analyze = async (id: number) => {
    setLoading(true);
    setError("");
    try {
      await api.post(`/leave/documents/${id}/analyze/`);
      await refresh(id);
    } catch (requestError: unknown) {
      await refresh(id).catch(() => undefined);
      setError(
        apiErrorMessage(
          requestError,
          "De weekstaat kon momenteel niet worden geanalyseerd.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };
  const upload = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const form = new FormData();
      form.append("file", file);
      if (organizationId) form.append("organization", String(organizationId));
      const response = await api.post<Document>("/leave/documents/upload/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocument(response.data);
      await analyze(response.data.id);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het document kon niet worden opgeslagen."),
      );
    } finally {
      setLoading(false);
    }
  };
  const confirmMapping = async (index: number, selection: string) => {
    if (!document || !selection) return;
    const ignore = selection === IGNORE_OPTION;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/leave/documents/${document.id}/confirm/`, {
        kind: "unknown_term",
        index,
        action: ignore ? "ignore" : "",
        leave_type_id: ignore ? null : Number(selection),
      });
      await refresh(document.id);
      setSuccess(
        ignore
          ? "Deze regel wordt voortaan genegeerd."
          : "Het verloftype is opgeslagen.",
      );
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De mapping kon niet worden opgeslagen."));
    } finally {
      setLoading(false);
    }
  };
  const confirmValue = async (index: number, value: string) => {
    if (!document || value === "") return;
    setLoading(true);
    setError("");
    try {
      await api.post(`/leave/documents/${document.id}/confirm/`, {
        kind: "leave_entry",
        index,
        value: Number(value),
      });
      await refresh(document.id);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De onzekere waarde kon niet worden bevestigd."),
      );
    } finally {
      setLoading(false);
    }
  };
  const decide = async (differenceId: number, decision: string) => {
    if (!document) return;
    setLoading(true);
    setError("");
    try {
      await api.post(
        `/leave/documents/${document.id}/differences/${differenceId}/decision/`,
        { decision },
      );
      await refresh(document.id);
      setSuccess("De beoordeling is opgeslagen.");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De beoordeling kon niet worden opgeslagen."),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="import-page">
      <PageHeader
        eyebrow="Verlofcontrole"
        title="Weekstaat controleren"
        description="Upload een PDF of Excel-bestand en vergelijk het met je verlofadministratie."
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <section className="surface import-panel">
        <div className="surface-heading">
          <div>
            <p className="eyebrow">Stap 1</p>
            <h2>Document uploaden</h2>
          </div>
          <FileCheck2 size={22} />
        </div>
        <form className="import-form" onSubmit={upload}>
          <label className="file-picker">
            <FileUp size={24} />
            <span>
              <strong>{file?.name ?? "Kies een weekstaat"}</strong>
              <small>PDF, XLSX of XLS, maximaal 10 MB</small>
            </span>
            <input
              type="file"
              accept=".pdf,.xlsx,.xls"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <button className="button primary" type="submit" disabled={!file || loading}>
            <FileUp size={17} /> {loading ? "Bezig..." : "Uploaden en controleren"}
          </button>
        </form>
        {document?.duplicate_document_ids.length ? (
          <p className="field-hint">
            Dit bestand lijkt al eerder te zijn geüpload. Je kunt toch doorgaan.
          </p>
        ) : null}
        {document?.same_period_document_ids.length ? (
          <p className="field-hint">
            Voor deze periode bestaat al een eerdere analyse. Controleer of dit een
            gecorrigeerde versie is.
          </p>
        ) : null}
      </section>
      {document && (
        <section className="surface import-panel">
          <div className="surface-heading">
            <div>
              <p className="eyebrow">Stap 2</p>
              <h2>{document.original_filename}</h2>
            </div>
            <span className={`member-status is-labelled status-${document.status}`}>
              {document.status_display || document.status}
            </span>
          </div>
          {document.error_message && (
            <ErrorNotice>{document.error_message}</ErrorNotice>
          )}
          <LeaveDocumentAnalysisSummary document={document} analysis={analysis} />
          {analysis?.normalized_data.unknown_terms?.map((term, index) => (
            <div className="form-row" key={`${term.raw_term}-${index}`}>
              <label>
                <span>Onbekend verloftype: {term.raw_term ?? term.value}</span>
                <div className="inline-form">
                  <select
                    value={mappingSelections[index] ?? ""}
                    onChange={(event) =>
                      setMappingSelections((current) => ({
                        ...current,
                        [index]: event.target.value,
                      }))
                    }
                    disabled={loading}
                  >
                    <option value="">Kies bestaand verloftype</option>
                    {leaveTypes.map((type) => (
                      <option value={type.id} key={type.id}>
                        {type.name}
                      </option>
                    ))}
                    <option value={IGNORE_OPTION}>Negeren (geen actie)</option>
                  </select>
                  <button
                    className="button subtle"
                    type="button"
                    disabled={loading || !mappingSelections[index]}
                    onClick={() =>
                      void confirmMapping(index, mappingSelections[index] ?? "")
                    }
                  >
                    Opslaan
                  </button>
                </div>
              </label>
            </div>
          ))}
          {document.status === "needs_input" && (
            <p className="field-hint">
              Bevestig eerst de onbekende verloftypen en onzekere waarden
              hieronder. Na de laatste bevestiging worden de uren vergeleken.
            </p>
          )}
          {analysis?.normalized_data.uncertain_values?.map((value, itemIndex) =>
            typeof value.index === "number" &&
            (value.kind ?? "leave_entry") === "leave_entry" ? (
              <div className="form-row" key={`uncertain-${value.index}-${itemIndex}`}>
                <label>
                  <span>
                    Onzekere waarde ({Math.round((value.confidence ?? 0) * 100)}%):{" "}
                    {value.source?.raw_text ?? "controleer de herkende verlofregel"}
                  </span>
                  <div className="inline-form">
                    <input
                      type="number"
                      step="0.5"
                      defaultValue={value.extracted_value ?? ""}
                      disabled={loading}
                    />
                    <button
                      className="button subtle"
                      type="button"
                      disabled={loading}
                      onClick={(event) => {
                        const input = event.currentTarget
                          .previousElementSibling as HTMLInputElement | null;
                        void confirmValue(value.index!, input?.value ?? "");
                      }}
                    >
                      Bevestigen
                    </button>
                  </div>
                </label>
              </div>
            ) : null,
          )}
          {analysis?.differences?.length ? (
            <div className="document-differences">
              <h3>Verschillen beoordelen</h3>
              {analysis.differences.map((difference) => {
                const matched = Math.abs(difference.difference.minutes ?? 0) <= 1;
                return (
                  <article
                    className={`document-difference ${
                      matched ? "is-match" : "is-difference"
                    }`}
                    key={difference.id}
                  >
                    <div>
                      <strong>
                        {[
                          difference.mutation_label,
                          difference.leave_type_name,
                          difference.transaction_date
                            ? formatDate(difference.transaction_date)
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </strong>
                      <small>
                        Werkgever: {hours(difference.document_value.minutes)} ·
                        Verlofapp: {hours(difference.app_value.minutes)} · Verschil:{" "}
                        {hours(difference.difference.minutes)}
                      </small>
                    </div>
                    {matched ? (
                      <Check size={18} />
                    ) : difference.status === "pending" ? (
                      <div className="document-actions">
                        <button
                          className="icon-button"
                          title="Mijn verlofadministratie klopt"
                          aria-label="Mijn verlofadministratie klopt"
                          onClick={() => void decide(difference.id, "app_correct")}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          className="icon-button"
                          title="Werkgever klopt, overnemen"
                          aria-label="Werkgever klopt, overnemen"
                          onClick={() =>
                            void decide(difference.id, "document_correct_applied")
                          }
                        >
                          <RefreshCw size={16} />
                        </button>
                        <button
                          className="icon-button"
                          title="Later beoordelen"
                          aria-label="Later beoordelen"
                          onClick={() => void decide(difference.id, "ignored")}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <span>{difference.status}</span>
                    )}
                  </article>
                );
              })}
            </div>
          ) : null}
          {document.status !== "processing" && (
            <button
              className="button subtle"
              type="button"
              disabled={loading}
              onClick={() => void analyze(document.id)}
            >
              <RefreshCw size={17} />{" "}
              {document.status === "failed" ? "Opnieuw proberen" : "Opnieuw analyseren"}
            </button>
          )}
        </section>
      )}
      <section className="surface import-panel document-history">
        <div className="surface-heading">
          <div>
            <p className="eyebrow">Historie</p>
            <h2>Eerder gecontroleerde week- en maandstaten</h2>
          </div>
          <Link className="button subtle" to="/app/documents/mappings">
            <ListChecks size={17} /> Matchcriteria beheren
          </Link>
          <History size={22} />
        </div>
        {historyLoading ? (
          <LoadingState label="Historie laden..." />
        ) : history.length === 0 ? (
          <div className="empty-state compact">
            <FileCheck2 size={25} />
            <h2>Nog geen uploads</h2>
            <p>Zodra je een week- of maandstaat controleert verschijnt die hier.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Bestandsnaam</th>
                  <th>Periode</th>
                  <th>Geüpload</th>
                  <th>Status</th>
                  <th>Match</th>
                  <th aria-label="Openen" />
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/app/documents/${row.id}`}>
                        <strong>{row.original_filename}</strong>
                      </Link>
                      {row.employer_name && <small>{row.employer_name}</small>}
                    </td>
                    <td>{periodLabel(row)}</td>
                    <td>{formatDate(row.uploaded_at.slice(0, 10))}</td>
                    <td>
                      <span
                        className={`member-status is-labelled status-${row.status}`}
                      >
                        {row.status_display || row.status}
                      </span>
                    </td>
                    <td>
                      {row.difference_counts.total === 0
                        ? "Geen vergelijking"
                        : row.fully_matched
                        ? "Alles gematcht"
                        : `${row.difference_counts.mismatched} verschil(len), ${row.difference_counts.open} open`}
                    </td>
                    <td>
                      <Link
                        className="icon-button"
                        to={`/app/documents/${row.id}`}
                        title="Gegevensmatch bekijken"
                        aria-label="Gegevensmatch bekijken"
                      >
                        <ChevronRight size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {loading && !document && <LoadingState />}
    </div>
  );
}
